import { useNavigate } from 'react-router-dom';
import { ContentBox } from 'app/agriculture/components/ContentBox/ContentBox';
import { Actions } from 'app/agriculture/components/Actions/Actions';
import { Controller, FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { AgricultureToastType } from 'app/agriculture/AgricultureToast/agriculture_toast_service';
import { FormDiscardChangesBlocker } from 'app/agriculture/forms/components/FormDiscardChangesBlocker/FormDiscardChangesBlocker';
import { entityConfigToType, InfraType } from 'app/agriculture/mutations/types/entityConfig';
import { AssetIcon } from 'app/agriculture/icons/components';
import { Input } from 'app/agriculture/forms/components/Input/Input';
import { InlineAlert } from 'app/agriculture/components/InlineAlert/InlineAlert';
import { useEntityUpdateMutation } from 'app/agriculture/mutations/entity/update/useEntityUpdateMutation';
import { useEntity } from 'app/agriculture/queries/entities/useEntity';
import { InfraSpecificFields } from 'app/agriculture/routes/Infra/components/InfraSpecificFields';
import { ShowOnMapButton } from 'app/agriculture/components/ContentBox/components/ShowOnMapButton';
import { convertGeometryToMapLocation } from 'app/agriculture/components/utils/convertGeometryToLocation/convertGeometryToMapLocation';
import { isBillboardEntityType } from 'app/agriculture/models/Entities';
import { RenderedMapMarker } from 'app/agriculture/components/Map/RenderedMapMarker/RenderedMapMarker';
import { ShapeEditorMapInput } from 'app/agriculture/forms/components/ShapeEditor/ShapeEditorMapInput';
import { convertMapLocationToPosition } from 'app/agriculture/components/utils/convertMapLocationToPosition';
import { ContentLayout } from 'app/agriculture/components/ContentLayout/ContentLayout';

interface InfraEditFormProps<T extends InfraType> {
    infraId: string;
    infraType: T;
}

export function InfraEditForm<T extends InfraType>({ infraType, infraId }: InfraEditFormProps<T>) {
    const entity = useEntity({ entityId: infraId, entityType: infraType });
    const navigate = useNavigate();
    const form = useForm<entityConfigToType[InfraType]>({
        mode: 'all',
        defaultValues: entity,
        shouldFocusError: true,
    });

    const { mutate, isPending } = useEntityUpdateMutation(infraType);

    const submitHandler: SubmitHandler<entityConfigToType[InfraType]> = (formData) => {
        mutate(
            { ...entity, ...formData },
            {
                onSuccess: () => {
                    form.reset();
                    navigate('../');
                },
            },
        );
    };

    return (
        <FormProvider {...form}>
            <ContentBox
                title={`Edit ${infraType}`}
                Icon={(props) => <AssetIcon {...props} name={infraType} />}
                showOnMapButton={
                    <ShowOnMapButton
                        position={convertMapLocationToPosition(
                            convertGeometryToMapLocation(entity.geometry),
                        )}
                    />
                }
                variant="md"
                forceAsideOpen
                aside={<ShapeEditorMapInput infraType={infraType} />}
            >
                <form onSubmit={form.handleSubmit(submitHandler)}>
                    <FormDiscardChangesBlocker />
                    {form.formState.errors.geometry?.message == null ? null : (
                        <ContentBox.Section>
                            <InlineAlert type={AgricultureToastType.ERROR}>
                                {form.formState.errors.geometry.message}
                            </InlineAlert>
                        </ContentBox.Section>
                    )}
                    <ContentBox.Section scrollable>
                        <ContentLayout>
                            <Input
                                label="Name"
                                {...form.register('name', {
                                    required: 'Please enter a name',
                                })}
                                errorMessage={form.formState.errors.name?.message}
                            />
                            <InfraSpecificFields infraType={infraType} />
                            {isBillboardEntityType(infraType) ? (
                                <Controller
                                    control={form.control}
                                    name="geometry"
                                    render={({ field }) => {
                                        return (
                                            <RenderedMapMarker
                                                value={convertGeometryToMapLocation(field.value)}
                                                onChange={({ latitude, longitude }) => {
                                                    field.onChange({
                                                        type: 'Point',
                                                        coordinates: [longitude, latitude],
                                                    });
                                                }}
                                            />
                                        );
                                    }}
                                />
                            ) : null}
                            <Actions
                                sticky
                                primaryAction={{
                                    text: 'Save changes',
                                    type: 'submit',
                                    isLoading: isPending,
                                }}
                                backAction={{
                                    text: 'Back',
                                    onClick: () => {
                                        navigate(-1);
                                    },
                                }}
                            />
                        </ContentLayout>
                    </ContentBox.Section>
                </form>
            </ContentBox>
        </FormProvider>
    );
}
